import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useFormik, FormikHelpers, yupToFormErrors } from "formik";
import {
  ShippingOption,
  StripePaymentMethod,
  TicketCoverPurchaseBehaviour,
  UpdateOrderInput,
} from "~graphql/sdk";
import { useEffect, useState } from "react";
import { useCheckoutContext } from "~context/checkoutContext";
import { useSDK } from "~hooks";
import { getError, handlePromise, showToast } from "~lib";
import * as Yup from "yup";
import { checkoutDebug } from "~lib/debug";
import { StripeExpressCheckoutElementConfirmEvent } from "@stripe/stripe-js";
import { useStripeConfigContext } from "~lib/stripeConfigContext";
import { extractCardholderName } from "~lib/helpers/extractCardHolderName";
import { camelCase, upperFirst } from "lodash";

const validationSchema: Yup.Schema<{
  ticketCover?: boolean;
}> = Yup.object({
  ticketCover: Yup.boolean().when("$requireTicketCoverSelection", {
    is: true,
    then: Yup.boolean().required("Please select an option"),
    otherwise: Yup.boolean().optional(),
  }),
});

export type SinglePageCheckoutFormValues = {
  ticketCover?: boolean;
  usableForMarketing: boolean;
};

const initialValues = (): SinglePageCheckoutFormValues => ({
  ticketCover: undefined,
  usableForMarketing: true,
});

export const useExpressCheckoutForm = () => {
  const stripe = useStripe();
  const elements = useElements();
  const sdk = useSDK();
  const { order } = useCheckoutContext();
  const config = useStripeConfigContext();
  const [paymentInProgress, setPaymentInProgress] = useState(false);

  const handleSubmit = (
    values: SinglePageCheckoutFormValues,
    actions: FormikHelpers<SinglePageCheckoutFormValues>
  ) => {
    // No-op because we handle submission via the Stripe Express Checkout confirm event
  };

  const formik = useFormik<SinglePageCheckoutFormValues>({
    initialValues: initialValues(),
    onSubmit: handleSubmit,
    validate: async (values) => {
      const ticketCover =
        order?.event?.ticketCover ?? order?.membership?.ticketCover;
      const requireTicketCoverSelection =
        ticketCover?.isEnabled &&
        ticketCover.purchaseBehaviour === TicketCoverPurchaseBehaviour.Optional;

      return validationSchema
        .validate(values, {
          abortEarly: false,
          context: {
            requireTicketCoverSelection,
          },
        })
        .then(() => undefined) // Map to `undefined` to indicate no errors.
        .catch(yupToFormErrors);
    },
    validateOnChange: false,
  });

  // Scroll to first error when submitting the form
  useEffect(() => {
    if (formik.isSubmitting && Object.keys(formik.errors).length > 0) {
      const firstErrorField = Object.keys(formik.errors)[0];

      const errorElement = document.getElementById(firstErrorField);

      if (errorElement) {
        // Focus the element (for inputs)
        errorElement.focus?.();

        errorElement.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    }
  }, [formik.isSubmitting, formik.errors]);

  async function handleConfirm(e: StripeExpressCheckoutElementConfirmEvent) {
    checkoutDebug("confirmed", e);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      console.error(submitError);
      return;
    }

    /*
    {
    "elementType": "expressCheckout",
    "expressPaymentType": "google_pay",
    "billingDetails": {
        "name": "Card Holder Name",
        "address": {
            "line1": "1600 Amphitheatre Parkway",
            "line2": null,
            "city": "Mountain View",
            "state": "CA",
            "postal_code": "94043",
            "country": "US"
        },
        "email": "jess.nz@gmail.com",
        "phone": "6505555555"
    }*/

    const { firstName, lastName } = extractCardholderName(
      e.billingDetails?.name
    );

    const updateOrderInput: UpdateOrderInput = {
      step: 3, // Skips buyer information validation, which is step 2
      deliveryMethod: ShippingOption.Eticket,
      buyerInformation: {
        // This is the name on the card
        firstName,
        lastName,
        email: e.billingDetails?.email,
        phoneNumber: e.billingDetails?.phone,
        state: e.billingDetails?.address?.state,
        postalCode: e.billingDetails?.address?.postal_code,
        country: e.billingDetails?.address?.country,
        city: e.billingDetails?.address?.city,
      },
      billingAddress: {
        line1: e.billingDetails?.address?.line1,
        line2: e.billingDetails?.address?.line2 || undefined,
        suburb: undefined,
        postalCode: e.billingDetails?.address?.postal_code,
        city: e.billingDetails?.address?.city,
        state: e.billingDetails?.address?.state,
        country: e.billingDetails?.address?.country,
      },
    };

    const { error: updateOrderError } = await handlePromise(async () =>
      sdk.updateOrder({
        id: order.id,
        input: updateOrderInput,
      })
    );

    if (updateOrderError) {
      // Silent fail because webhook will update order details.
      // The purpose of capturing here is so that we have the users email as early as possible.
    }

    const { error: createPaymentError, data } = await handlePromise(async () =>
      sdk.createPayment({
        id: order.id,
        input: {
          gateway: config.integrationId,
          usableForMarketing: formik.values.usableForMarketing,
          gatewayOptions: {
            expressCheckoutPaymentMethod: upperFirst(
              camelCase(e.expressPaymentType)
            ) as StripePaymentMethod,
          },
        },
      })
    );

    if (createPaymentError) {
      return showToast(getError(createPaymentError, "graphQL"), "error");
    }

    if (data.createPayment.result?.__typename === "PaymentError") {
      return showToast(data.createPayment.result.message, "error");
    }

    const redirectTo = process.env.STRIPE_EXPRESS_CHECKOUT_TEST_DOMAIN
      ? `${process.env.STRIPE_EXPRESS_CHECKOUT_TEST_DOMAIN}/checkout/${order.id}`
      : data.createPayment?.result?.redirectTo;

    setPaymentInProgress(true);

    try {
      const { error } = await stripe.confirmPayment({
        elements,
        clientSecret: data.createPayment?.result?.externalId,
        confirmParams: {
          return_url: redirectTo + "?expressCheckout=true",
        },
      });

      if (error) {
        // This point is only reached if there's an immediate error when
        // confirming the payment. Show the error to your customer (for example, payment details incomplete)
        console.log(error);
        showToast("Could not confirm payment. Please try again", "error");
        setPaymentInProgress(false);
      } else {
        // The payment UI automatically closes and the customer is redirected.
      }
    } catch (err) {
      console.error(err);
      showToast("Could not confirm payment. Please try again", "error");
      setPaymentInProgress(false);
    }
  }

  return {
    formik,
    handleConfirm,
    paymentInProgress,
  };
};
